/**
 * NOLAS 2026 — © Mikael (Mikaël FOURNY). Fork de la plateforme TNE 2026.
 * src/i18n/fr.roster.ts
 *
 * Rôle :
 *   Sous-catalogue i18n du chantier « roster ». Fusionné dans `messagesFr`
 *   par `i18n/fr.ts`, donc utilisable exactement comme n'importe quelle clé.
 *
 * Pourquoi un fichier SÉPARÉ plutôt que d'allonger `fr.ts` :
 *   `fr.ts` fait ~6 700 lignes et c'est LA dépendance partagée du dépôt.
 *   Plusieurs chantiers menés en parallèle qui y écrivent en même temps se
 *   clobberaient — leçon du lot Console C9 : les agents parallèles ne se
 *   gênent pas sur LEURS fichiers, ils se gênent sur les fichiers COMMUNS.
 *   Un catalogue par chantier supprime la collision par construction.
 *
 * ⚠ Règles :
 *   • Wording français strict (§15.5), apostrophe typographique `’`.
 *   • JAMAIS le mot « atelier » en dur : employer les tokens du lexique
 *     (`{lexWorkshopPlural}`…) — cf. utils/eventLexicon.ts. Un test échoue
 *     sinon.
 *   • Aucune apostrophe DROITE juste avant un `{token}` (échappement ICU).
 *
 * @author Mikael
 * @since  2026-08-03 (lot CIRFA)
 */

export const messagesRosterFr: Record<string, string> = {
  // ───────────────────────────────────────────────────────────────────────
  // Statuts d'une ligne
  // ───────────────────────────────────────────────────────────────────────
  'roster.status.draft': 'Brouillon',
  'roster.status.submitted': 'Transmise',
  'roster.status.validated': 'Validée',
  'roster.status.rejected': 'Refusée',
  'roster.status.unknown': 'Statut inconnu',

  // ───────────────────────────────────────────────────────────────────────
  // Statut d'ensemble d'une liste (dérivé des lignes, jamais stocké)
  // ───────────────────────────────────────────────────────────────────────
  'roster.groupStatus.empty': 'Aucune personne annoncée',
  'roster.groupStatus.draft': 'En cours de saisie',
  'roster.groupStatus.submitted': 'Transmise à l’organisation',
  'roster.groupStatus.validated': 'Validée pour le contrôle',
  'roster.groupStatus.rejected': 'Correction demandée',
  'roster.groupStatus.partial': 'Transmission partielle',

  // ───────────────────────────────────────────────────────────────────────
  // Types de pièce d'identité DÉCLARÉS (jamais de numéro — RGPD)
  // ───────────────────────────────────────────────────────────────────────
  'roster.idDocument.none': 'Non déclarée',
  'roster.idDocument.cni': 'Carte nationale d’identité',
  'roster.idDocument.passeport': 'Passeport',
  'roster.idDocument.titre_sejour': 'Titre de séjour',
  'roster.idDocument.permis_conduire': 'Permis de conduire',
  'roster.idDocument.carte_scolaire': 'Carte scolaire ou étudiante',
  'roster.idDocument.autre': 'Autre pièce officielle',
  'roster.idDocument.unknown': 'Type non reconnu',

  // ───────────────────────────────────────────────────────────────────────
  // Motifs de rejet d'une ligne à l'import
  // ───────────────────────────────────────────────────────────────────────
  'roster.import.error.empty_file': 'Fichier vide.',
  'roster.import.error.missing_last_name': 'Nom manquant.',
  'roster.import.error.missing_first_name': 'Prénom manquant.',
  'roster.import.error.invalid_birth_date':
    'Date de naissance illisible (attendu : 12/03/2009 ou 2009-03-12).',
  'roster.import.error.unknown_id_document_type':
    'Type de pièce non reconnu (CNI, passeport, titre de séjour…).',
  'roster.import.error.too_many_rows':
    'Fichier trop long : au-delà de 1 000 lignes, découpez-le.',

  // ───────────────────────────────────────────────────────────────────────
  // Champs et colonnes, communs aux deux écrans
  // ───────────────────────────────────────────────────────────────────────
  'roster.field.lastName': 'Nom',
  'roster.field.firstName': 'Prénom',
  'roster.field.birthDate': 'Date de naissance',
  'roster.field.idDocument': 'Pièce d’identité déclarée',
  'roster.field.accompanist': 'Accompagnateur',
  'roster.field.notes': 'Remarques',
  'roster.field.status': 'Statut',
  'roster.field.actions': 'Actions',
  'roster.field.birthDateHelp': 'Facultatif. Format 12/03/2009.',
  'roster.field.idDocumentHelp':
    'Seul le TYPE de pièce est enregistré. Le numéro n’est jamais demandé.',
  'roster.field.notesHelp': 'Lu par l’organisation. Aucune donnée de santé.',
  'roster.field.idDocumentEmpty': '— Non déclarée —',

  // ───────────────────────────────────────────────────────────────────────
  // Actions
  // ───────────────────────────────────────────────────────────────────────
  'roster.action.add': 'Ajouter une personne',
  'roster.action.edit': 'Modifier',
  'roster.action.delete': 'Retirer',
  'roster.action.save': 'Enregistrer',
  'roster.action.cancel': 'Annuler',
  'roster.action.validate': 'Valider',
  'roster.action.reject': 'Refuser',
  'roster.action.reopen': 'Renvoyer en brouillon',
  'roster.action.submit': 'Transmettre la liste',
  'roster.action.export': 'Exporter en CSV',
  'roster.action.exportAll': 'Exporter toutes les listes',
  'roster.action.back': 'Retour aux groupes',
  'roster.action.import': 'Importer un fichier CSV',

  // ───────────────────────────────────────────────────────────────────────
  // États génériques
  // ───────────────────────────────────────────────────────────────────────
  'roster.state.loading': 'Chargement de la liste…',
  'roster.state.error': 'La liste n’a pas pu être chargée. Réessayez.',
  'roster.state.empty':
    'Aucune personne annoncée pour l’instant. Ajoutez-les une par une, ou importez un fichier.',

  // ───────────────────────────────────────────────────────────────────────
  // Doublons
  // ───────────────────────────────────────────────────────────────────────
  'roster.duplicates.title': 'Doublons probables',
  'roster.duplicates.count':
    '{count, plural, one {# doublon probable détecté} other {# doublons probables détectés}}.',
  'roster.duplicates.help':
    'Deux personnes portent les mêmes nom et prénom. Vérifiez avant de transmettre : une personne annoncée deux fois est contrôlée deux fois au poste de garde.',

  // ───────────────────────────────────────────────────────────────────────
  // Import CSV
  // ───────────────────────────────────────────────────────────────────────
  'roster.import.title': 'Importer une liste',
  'roster.import.help':
    'Fichier CSV exporté depuis un tableur. Colonnes reconnues : Nom, Prénom, Date de naissance, Pièce. Le séparateur point-virgule ou virgule est détecté automatiquement.',
  'roster.import.pick': 'Choisir un fichier',
  'roster.import.previewTitle': 'Aperçu avant import',
  'roster.import.previewCount':
    '{count, plural, one {# ligne exploitable} other {# lignes exploitables}}.',
  'roster.import.previewErrors':
    '{count, plural, one {# ligne ignorée} other {# lignes ignorées}}.',
  'roster.import.errorLine': 'Ligne {line} — {reason}',
  'roster.import.confirm': 'Importer ces lignes',
  'roster.import.doneInserted':
    '{count, plural, one {# personne ajoutée} other {# personnes ajoutées}}.',
  'roster.import.doneDuplicates':
    '{count, plural, one {# doublon ignoré} other {# doublons ignorés}}.',
  'roster.import.readError': 'Le fichier n’a pas pu être lu.',
  'roster.import.noRow':
    'Aucune ligne exploitable dans ce fichier. Vérifiez qu’il contient au moins un nom et un prénom.',

  // ───────────────────────────────────────────────────────────────────────
  // Écran ADMIN
  // ───────────────────────────────────────────────────────────────────────
  'roster.admin.title': 'Listes nominatives',
  'roster.admin.lead':
    'Personnes annoncées par groupe, pour le contrôle d’accès du jour J.',
  'roster.admin.secureOff':
    'Le mode « site sécurisé » est désactivé pour cet événement : aucune obligation n’est affichée aux inscrits. Les listes restent consultables. Le réglage se trouve dans Paramètres, section Sécurité.',
  'roster.admin.groupsEmpty':
    'Aucun groupe n’est encore créé. Créez-les dans la section Groupes, puis désignez un accompagnateur.',
  'roster.admin.colGroup': 'Groupe',
  'roster.admin.colReferent': 'Accompagnateur',
  'roster.admin.colTotal': 'Annoncés',
  'roster.admin.colToReview': 'À arbitrer',
  'roster.admin.noReferent': 'Aucun accompagnateur désigné',
  'roster.admin.open': 'Ouvrir la liste',
  'roster.admin.detailTitle': 'Liste de {group}',
  'roster.admin.bulkValidate': 'Tout valider',
  'roster.admin.bulkReopen': 'Tout renvoyer en brouillon',
  'roster.admin.rejectTitle': 'Refuser cette ligne',
  'roster.admin.rejectReason': 'Motif du refus',
  'roster.admin.rejectReasonHelp':
    'Ce motif est lu par l’accompagnateur : dites-lui quoi corriger.',
  'roster.admin.rejectRequired': 'Un motif est obligatoire pour refuser.',
  'roster.admin.deleteConfirm':
    'Retirer {name} de la liste ? Cette personne ne sera plus annoncée au contrôle.',

  // ───────────────────────────────────────────────────────────────────────
  // Écran ACCOMPAGNATEUR
  // ───────────────────────────────────────────────────────────────────────
  // ── Espace accompagnateur — compléments (écran MonGroupeRosterPage) ────
  // Ajoutés après coup : le chantier avait posé la nomenclature et les libellés
  // de l'écran d'administration, ces quelques clés manquaient au parcours du
  // référent lui-même.
  'roster.me.pickGroup': 'Groupe à préparer',
  'roster.toast.created': 'Personne ajoutée à la liste.',
  'roster.toast.updated': 'Modification enregistrée.',
  'roster.toast.deleted': 'Personne retirée de la liste.',
  'roster.toast.error': 'L’enregistrement a échoué. Réessayez dans quelques instants.',
  'roster.toast.importError':
    'L’import a échoué. Vérifiez que le fichier est bien un CSV et réessayez.',
  'roster.toast.imported':
    '{inserted, plural, =0 {Aucune ligne importée} one {1 ligne importée} other {# lignes importées}}{duplicates, plural, =0 {} one { · 1 doublon ignoré} other { · # doublons ignorés}}{errors, plural, =0 {} one { · 1 ligne en erreur} other { · # lignes en erreur}}.',
  'roster.submit.idReminder':
    'Rappel : chaque personne de cette liste devra présenter une pièce d’identité en cours de validité le jour de l’événement.',
  'roster.me.title': 'Ma liste nominative',
  'roster.me.lead':
    'L’événement se tient sur un site à accès contrôlé. Transmettez la liste des personnes de votre groupe avant votre venue.',
  'roster.me.noGroup':
    'Aucun groupe ne vous est rattaché. Contactez l’organisation pour être désigné accompagnateur de votre groupe.',
  'roster.me.groupsTitle': 'Mes groupes',
  'roster.me.groupCount':
    '{count, plural, =0 {aucune personne annoncée} one {# personne annoncée} other {# personnes annoncées}}',
  'roster.me.locked':
    'Liste transmise à l’organisation : elle n’est plus modifiable. Demandez à l’organisation de la renvoyer en brouillon si vous devez la corriger.',
  'roster.me.rejectedNotice':
    'L’organisation demande une correction sur au moins une ligne. Corrigez-la, puis transmettez à nouveau.',
  'roster.me.rejectionReason': 'Motif : {reason}',
  'roster.me.submitConfirm':
    'Transmettre la liste à l’organisation ? Vous ne pourrez plus la modifier ensuite.',
  'roster.me.submitDone':
    '{count, plural, one {# ligne transmise} other {# lignes transmises}}.',
  'roster.me.deleteConfirm': 'Retirer {name} de votre liste ?',
  'roster.me.addTitle': 'Ajouter une personne',
  'roster.me.editTitle': 'Modifier une personne',

  // ───────────────────────────────────────────────────────────────────────
  // Rappel « pièce d'identité obligatoire » (parcours d'inscription)
  // ⚠ `{lexVisitorBare}` : « visiteur » sur un salon, « élève » sur un forum
  //   scolaire. Le vocabulaire est piloté par le lexique de l'événement.
  // ───────────────────────────────────────────────────────────────────────
  'roster.idNotice.title': 'Pièce d’identité obligatoire',
  'roster.idNotice.default':
    'L’événement se tient sur un site à accès contrôlé. Chaque {lexVisitorBare} doit se présenter muni d’une pièce d’identité en cours de validité : carte nationale d’identité, passeport ou titre de séjour. Sans pièce, l’accès sera refusé au poste de contrôle.',
  'roster.idNotice.privacy':
    'Le numéro de votre pièce n’est jamais demandé ni conservé : seul son type déclaré est enregistré.',
};
